<script setup lang="ts">
const { $containment } = useNuxtApp();
const { getValidationModule } = useModulesValidation();

// Enable or disable the display of the debug
// Change to `false` if you don't want to display the debug
const IS_DEBUG_ENABLED = true;

// Controls if the debug is open or closed
const debugIsOpen = ref(true);

// Toggle the display of the debug
const toggleDebug = () => {
  debugIsOpen.value = !debugIsOpen.value;
};

// Get the current pendency name
const currentPendencyName = computed(() => {
  return $containment.currentPendency;
});

// Get the current context name
const currentContextName = computed(() => {
  return $containment.currentContext;
});

// Get the current context modules
const currentContextModules = computed(() => {
  if (!currentContextName.value) return [];

  return getValidationModule(
    currentContextName.value as keyof ValidationModules,
  );
});

// Get the validation state of all modules
const validationState = computed(() => {
  return $containment.state;
});

// Get the modules information of all contexts
const contextModulesInfo = computed(() => {
  return Object.keys(validationState.value).reduce(
    (acc, context) => {
      acc[context] = getValidationModule(context as keyof ValidationModules);

      return acc;
    },
    {} as Record<string, any[]>,
  );
});

// Get the validation status of a specific module
const getModuleStatus = (contextName: string | null, moduleName: string) => {
  if (
    !contextName ||
    !validationState.value[contextName as keyof Containment.Context]
  )
    return 'pending';

  const contextValidations =
    validationState.value[contextName as keyof Containment.Context];

  if (!contextValidations) return 'pending';

  const isValid =
    contextValidations.find((v) => v.module === moduleName)?.isValid ?? false;

  const module = contextModulesInfo.value[contextName]?.find(
    (m) => m.name === moduleName,
  );

  if (module?.frequent) return 'always';

  return isValid ? 'validated' : 'pending';
};

// Check if a module has special badges
const getModuleBadges = (module: any) => {
  return {
    frequent: module.frequent,
    force: module.force,
  };
};

// Sort the contexts by the presence of frequent modules
const sortedValidationState = computed(() => {
  return Object.entries(validationState.value).sort(
    ([aName, aState], [bName, bState]) => {
      const aHasFrequent =
        Array.isArray(aState) &&
        aState.some(
          (v) =>
            contextModulesInfo.value[aName]?.find((m) => m.name === v.module)
              ?.frequent,
        );

      const bHasFrequent =
        Array.isArray(bState) &&
        bState.some(
          (v) =>
            contextModulesInfo.value[bName]?.find((m) => m.name === v.module)
              ?.frequent,
        );

      if (aHasFrequent && !bHasFrequent) return 1;
      if (!aHasFrequent && bHasFrequent) return -1;

      return 0;
    },
  );
});
</script>

<template>
  <div
    v-if="IS_DEBUG_ENABLED"
    :class="[
      $style.debugWrapper,
      { [$style.closed]: !debugIsOpen },
      `transition`,
    ]"
  >
    <div :class="$style.debugInfo">
      <div :class="$style.debugContent">
        <div :class="$style.debugSection">
          <div :class="$style.debugStatus">
            <div :class="$style.debugStatusTitle">
              Debug enabled:
              <strong class="text-success">
                {{ IS_DEBUG_ENABLED }}
              </strong>
            </div>

            <div :class="$style.debugStatusPath">
              /components/auth/verification/Debug.vue
            </div>
          </div>

          <h3 :class="$style.debugTitle">
            <span>Current Context</span>
          </h3>

          <div :class="$style.card">
            <div :class="$style.cardHeader">
              <div :class="$style.cardTitle">
                Context name:
                <strong class="text-primary">
                  {{ currentContextName || 'no context' }}
                </strong>
              </div>

              <div
                v-if="currentPendencyName"
                :class="$style.cardSubtitle"
              >
                Current Pendency:
                <strong class="text-white">
                  {{ currentPendencyName }}
                </strong>
              </div>
            </div>

            <div
              v-if="currentContextModules?.length"
              :class="$style.cardContent"
            >
              <div
                v-for="module in currentContextModules"
                :key="module.name"
                :class="$style.moduleCard"
              >
                <div :class="$style.moduleInfo">
                  <span :class="$style.moduleName">
                    {{ module.name }}
                  </span>

                  <div :class="$style.badges">
                    <span
                      v-if="getModuleBadges(module).frequent"
                      :class="$style.badgeFrequent"
                    >
                      F
                    </span>
                    <span
                      v-if="getModuleBadges(module).force"
                      :class="$style.badgeForce"
                    >
                      !
                    </span>
                  </div>
                </div>

                <span
                  :class="[
                    $style.moduleStatus,
                    $style[getModuleStatus(currentContextName, module.name)],
                  ]"
                >
                  {{ getModuleStatus(currentContextName, module.name) }}
                </span>
              </div>
            </div>

            <div
              v-else
              class="text-texts/50 italic p-4"
            >
              no modules
            </div>
          </div>
        </div>

        <div :class="$style.debugSection">
          <h3 :class="$style.debugTitle">
            <span>All Modules and Status</span>
          </h3>

          <div
            v-for="[contextName, contextState] in sortedValidationState"
            :key="contextName"
            :class="$style.card"
          >
            <div :class="$style.cardHeader">
              <div :class="$style.cardTitle">
                {{ contextName }}

                <span
                  v-if="currentContextName === contextName"
                  :class="$style.contextActive"
                >
                  (current)
                </span>
              </div>
            </div>

            <div :class="$style.cardContent">
              <template
                v-if="Array.isArray(contextState) && contextState.length"
              >
                <div
                  v-for="validation in contextState"
                  :key="validation.module"
                  :class="$style.moduleCard"
                >
                  <div :class="$style.moduleInfo">
                    <span :class="$style.moduleName">
                      {{ validation.module }}
                    </span>

                    <div :class="$style.badges">
                      <span
                        v-if="
                          getModuleBadges(
                            contextModulesInfo[contextName]?.find(
                              (m) => m.name === validation.module,
                            ),
                          ).frequent
                        "
                        :class="$style.badgeFrequent"
                      >
                        F
                      </span>

                      <span
                        v-if="
                          getModuleBadges(
                            contextModulesInfo[contextName]?.find(
                              (m) => m.name === validation.module,
                            ),
                          ).force
                        "
                        :class="$style.badgeForce"
                      >
                        !
                      </span>
                    </div>
                  </div>

                  <span
                    :class="[
                      $style.moduleStatus,
                      $style[getModuleStatus(contextName, validation.module)],
                    ]"
                  >
                    {{ getModuleStatus(contextName, validation.module) }}
                  </span>
                </div>
              </template>

              <div
                v-else
                class="text-texts/50 italic p-1.5"
              >
                no modules
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <button
      :class="$style.toggleButton"
      @click="toggleDebug"
    >
      <NuxtIcon
        class="text-white"
        :name="debugIsOpen ? 'arrows/angle_left' : 'arrows/angle_right'"
      />
    </button>
  </div>
</template>

<style module lang="scss">
.debugWrapper {
  @apply fixed z-[999999] left-0 top-0 bottom-0 translate-x-0 transition-all duration-500;

  &.closed {
    @apply -translate-x-[280px];
  }

  .toggleButton {
    @apply absolute left-full top-4 bg-black/90 text-white w-8 h-10 flex items-center justify-center rounded-r-md cursor-pointer hover:bg-black/70 transition-colors;
  }

  .debugInfo {
    @apply h-full w-[290px];

    ::-webkit-scrollbar {
      width: 4px;
      height: 4px;
    }

    ::-webkit-scrollbar-track {
      @apply bg-white/10;
    }

    ::-webkit-scrollbar-thumb {
      @apply bg-white/15 border-transparent;
    }

    .debugContent {
      @apply h-full w-full bg-black/90 p-4 text-white text-sm overflow-auto;

      .debugSection {
        @apply mb-4;

        &:last-child {
          @apply mb-0;
        }

        .debugStatus {
          @apply text-xs px-3 py-2 bg-white/10 rounded-md mb-3 border border-solid border-white/10;

          .debugStatusTitle {
            @apply font-medium text-white/40;
          }

          .debugStatusPath {
            @apply text-2xs text-white/40 mt-1;
          }
        }

        .debugTitle {
          @apply font-bold text-sm mb-3 text-white/40;
        }

        .card {
          @apply rounded-lg overflow-hidden mb-2 last:mb-0 text-xs border border-solid border-white/20;

          .cardHeader {
            @apply px-3 py-2 bg-white/20;

            .cardTitle {
              @apply font-semibold;
            }

            .cardSubtitle {
              @apply text-xs text-white/70;
            }

            .contextActive {
              @apply text-xs text-success;
            }
          }

          .cardContent {
            @apply p-0 bg-white/5;

            .moduleCard {
              @apply flex items-center justify-between p-2 rounded border-t border-white/10 border-solid;

              .moduleInfo {
                @apply flex items-center;

                .moduleName {
                  @apply font-medium;
                }

                .badges {
                  @apply flex gap-1;

                  .badgeFrequent {
                    @apply text-[10px] bg-warning/30 text-warning px-1 py-0  font-medium ml-2 rounded-md;
                  }

                  .badgeForce {
                    @apply text-[10px] bg-error/30 text-error px-1.5 py-0.5 rounded font-medium;
                  }
                }
              }

              .moduleStatus {
                @apply px-1 py-0 rounded text-2xs font-bold;

                &.pending,
                &.always {
                  @apply bg-error text-white;
                }

                &.validated {
                  @apply bg-success text-white;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
